import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import Autocomplete from '@mui/material/Autocomplete';
import InputBase from '@mui/material/InputBase';
import ListItem from '@mui/material/ListItem';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import { makeStyles } from 'tss-react/mui';

import { payeesSelectors } from 'app-components/flux/payees';
import HighlightText from 'components/HighlightText';

import { noop } from 'lodash';
import { treatEnterAsTab } from 'utils/utils';

const useStyles = makeStyles()((theme) => ({
  inputRoot: {
    minWidth: 120,
    paddingLeft: theme.spacing(1),
    fontSize: theme.palette.register.fontSize.default,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  popper: {
    minWidth: 250,
  },
  option: {
    padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`,
  },
  groupLabel: {
    ...theme.typography.caption,
    paddingBottom: theme.spacing(0.5),
  },
  allowCreateContainer: {
    display: 'flex',
    alignItems: 'center',
    padding: `${theme.spacing(1)} ${theme.spacing(0.5)}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    position: 'sticky',
    top: 0,
    left: 0,
  },
}));

const BoldHighlight = styled.b`
  color: ${({ theme }) => theme.palette.text.primary} !important;
`;

// static functions for rendering options/groups
const groupByKey = (op) => {
  switch (op.groupKey) {
    case 'EXISTING_PAYEE':
      return 'Choose from existing';
    case 'NEW_PAYEE':
      return 'Create new payee';
    default:
      return null;
  }
};
const getOptionLabel = (op) => (typeof op === 'string' ? op : op.label);

/**
 * Custom filter that ranks matches by relevance:
 * 1. Exact match
 * 2. Starts with input
 * 3. Word starts with input (any word in name starts with input)
 * 4. Multi-token ordered (all tokens appear in sequence)
 * 5. Multi-token unordered (all tokens present, any order)
 * 6. Contains (substring anywhere)
 */
const filterOptions = (options, { inputValue }) => {
  const input = inputValue.trim().toLowerCase();
  if (!input) return options;

  const tokens = input.split(/\s+/).filter(Boolean);
  const isMultiToken = tokens.length > 1;

  const exact = [];
  const startsWith = [];
  const wordStartsWith = [];
  const multiTokenOrdered = [];
  const multiTokenUnordered = [];
  const contains = [];

  for (const option of options) {
    // Keep "NEW_PAYEE" options at the top
    if (option.groupKey === 'NEW_PAYEE') {
      exact.unshift(option);
      continue;
    }

    const name = (typeof option === 'string' ? option : option.name || '').toLowerCase();

    // 1. Exact match
    if (name === input) {
      exact.push(option);
      continue;
    }

    // 2. Starts with
    if (name.startsWith(input)) {
      startsWith.push(option);
      continue;
    }

    // 3. Word starts with (any word in the name starts with input)
    const words = name.split(/\s+/);
    if (words.some((word) => word.startsWith(input))) {
      wordStartsWith.push(option);
      continue;
    }

    // Multi-token matching
    if (isMultiToken) {
      // Check if all tokens are present
      const allTokensPresent = tokens.every((token) => name.includes(token));

      if (allTokensPresent) {
        // 4. Check if tokens appear in order
        let lastIndex = -1;
        let inOrder = true;
        for (const token of tokens) {
          const idx = name.indexOf(token, lastIndex + 1);
          if (idx === -1 || idx <= lastIndex) {
            inOrder = false;
            break;
          }
          lastIndex = idx;
        }

        if (inOrder) {
          multiTokenOrdered.push(option);
        } else {
          multiTokenUnordered.push(option);
        }
        continue;
      }
    }

    // 6. Contains (substring anywhere)
    if (name.includes(input)) {
      contains.push(option);
    }
  }

  return [...exact, ...startsWith, ...wordStartsWith, ...multiTokenOrdered, ...multiTokenUnordered, ...contains];
};
const renderOption = (props, option, state) =>
  option.isNewPayee ? (
    <li {...props} style={{ marginBottom: 8 }}>
      {option.label}
    </li>
  ) : (
    <li {...props}>
      <HighlightText highlightComponent={BoldHighlight} text={option.label} searchKeys={[state.inputValue]} />
    </li>
  );

const getOptionSelected = (op, value) => op.label === value && op.groupKey !== 'NEW_PAYEE';
export default function SPayeeField(props) {
  const {
    onChange,
    textFieldProps = {},
    defaultValue,
    inRegister,
    plain = false,
    autoFocus,
    tabOnEnter,
    tabSelectsHighlighted = false,
    fullWidth,
    header,
    ...otherProps
  } = props;

  const { classes } = useStyles();
  const [localValue, setLocalValue] = useState(defaultValue || '');
  const [open, setOpen] = useState(false);
  const highlightedOptionRef = useRef(null);

  useEffect(() => setLocalValue(defaultValue || ''), [defaultValue]); // update value if default changes

  // Building options array to pass to Autocomplete
  const options = useSelector(payeesSelectors.getPayeesNamesSorted)
    ?.toArray()
    .map((payee, index) => ({ groupKey: 'EXISTING_PAYEE', name: payee, label: payee, index }));
  const trimmedValue = localValue?.trim() || '';
  if (trimmedValue.length > 0 && !options.find((item) => item.name === trimmedValue)) {
    // if we want to show the user they can create a new payee
    options.unshift({ groupKey: 'NEW_PAYEE', name: trimmedValue, label: trimmedValue, isNewPayee: true });
  }

  const handleChange = (event, updatedValue, reason) => {
    if (reason === 'selectOption' || reason === 'blur') {
      if (event.key === 'Enter') {
        // maintain focus
        event.preventDefault();
        event.stopPropagation();
      }

      // use the current field value rather than the highlighted value on blur
      let currentVal = reason === 'blur' ? localValue : updatedValue || localValue;
      currentVal = (typeof currentVal === 'string' ? currentVal : currentVal?.label) || '';
      if (currentVal !== defaultValue) {
        onChange?.(currentVal);
      } else {
        setLocalValue(currentVal.trimStart());
      }
      // close paper on select or blur
      setOpen(false);
    }
  };

  const handleInputChange = (_e, value, reason) => {
    setLocalValue(value.trimStart() || '');
    if (reason !== 'reset') {
      setOpen(true);
    }
  };

  const handleBlur = () => setOpen(false);

  const handleHighlightChange = (_event, option) => {
    highlightedOptionRef.current = option;
  };

  const handleKeyDown = (e) => {
    // Tab selects the highlighted option (like Enter) when tabSelectsHighlighted is enabled
    if (tabSelectsHighlighted && e.key === 'Tab' && open && highlightedOptionRef.current) {
      const selectedValue = highlightedOptionRef.current;
      handleChange(e, selectedValue, 'selectOption');
      return;
    }
    // need to force change because register doesn't blur on tab
    if (inRegister && e.key === 'Tab' && localValue.length > 0) {
      handleChange(e, localValue, 'blur');
    }
    if (tabOnEnter && e.key === 'Enter') {
      // need to wrap in a timeout so that AutoComplete's 'onChange' can complete with 'selectOption'
      setTimeout(() => treatEnterAsTab(e), 0);
    }
    if (e.key === 'Escape') {
      // exit
      setLocalValue(defaultValue || '');
      onChange?.(defaultValue);
      setTimeout(() => e.target.blur(), 0);
    }
  };

  // TODO: perhaps after old register is phased out, remove the 'inRegister' logic (deprecatedRegister)
  const optionalClasses = inRegister
    ? {
        input: null,
        inputRoot: classes.inputRoot,
        groupLabel: classes.groupLabel,
        option: classes.option,
      }
    : {};

  return (
    <Autocomplete
      sharedcomponentid='SPayeeField'
      value={defaultValue || ''}
      inputValue={localValue}
      open={open}
      onChange={handleChange}
      onInputChange={handleInputChange}
      onBlur={handleBlur}
      onHighlightChange={handleHighlightChange}
      groupBy={groupByKey}
      getOptionLabel={getOptionLabel}
      options={options}
      renderTags={noop}
      filterOptions={filterOptions}
      renderOption={renderOption}
      isOptionEqualToValue={getOptionSelected}
      fullWidth={fullWidth}
      selectOnFocus
      autoComplete
      autoSelect
      autoHighlight
      freeSolo
      disableClearable
      disableCloseOnSelect
      // eslint-disable-next-line react/no-unstable-nested-components
      PaperComponent={
        header
          ? (
              { children, ...paperProps }, // by intercepting the PaperComponent we can inject options to keep at the top
            ) => (
              <Paper elevation={12} {...paperProps} style={{ pointerEvents: 'auto' }}>
                <ListItem
                  className={classes.allowCreateContainer}
                  style={{ pointerEvents: 'auto' }}
                  onMouseDown={(event) => {
                    // Prevent default to stop ClickAwayListener from closing the dropdown
                    event.preventDefault();
                  }}
                >
                  {header}
                </ListItem>
                {children}
              </Paper>
            )
          : Paper
      }
      // eslint-disable-next-line @typescript-eslint/naming-convention
      renderInput={({ InputProps, InputLabelProps, ...otherParams }) =>
        plain ? (
          <InputBase
            {...InputProps}
            {...otherParams}
            autoFocus={autoFocus}
            variant='paragraphRegular'
            margin='none'
            onFocus={(event) => event.target.select()} // select all text in the field
            onKeyDown={handleKeyDown}
          />
        ) : (
          <TextField
            {...otherParams}
            autoFocus={autoFocus}
            InputLabelProps={InputLabelProps}
            onKeyDown={handleKeyDown}
            placeholder={'Select payee'}
            label={'Payee'}
            variant='filled'
            margin={inRegister ? 'none' : 'normal'}
            {...textFieldProps}
            InputProps={{
              ...InputProps,
              ...(textFieldProps?.InputProps || {}),
            }}
          />
        )
      }
      {...otherProps}
      classes={{
        ...optionalClasses,
        popper: classes.popper,
        ...(otherProps.classes || {}),
      }}
    />
  );
}

SPayeeField.propTypes = {
  textFieldProps: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  defaultValue: PropTypes.string,
  inRegister: PropTypes.bool,
  plain: PropTypes.bool,
  fullWidth: PropTypes.bool,
  autoFocus: PropTypes.bool,
  tabOnEnter: PropTypes.bool,
  tabSelectsHighlighted: PropTypes.bool,
  header: PropTypes.node,
  // ferry props
  id: PropTypes.any,
  popupIcon: PropTypes.node,
  className: PropTypes.string,
};
